import {
    Box,
    FormSelectProps,
    FormSelect as IcebergFormSelect,
    components as ReactSelectComponents,
    SelectOption
} from '@auviknetworks/iceberg-ui';
import { CircularProgress } from '@mui/material';
import { Image } from 'application/Shared/Components/Image';
import { useFormFactoryContext } from 'application/Shared/Contexts/FormFactoryContext';
import { debug } from 'application/Shared/Utils';
import {
    RunRecipeParams,
    useRunCustomerRecipes
} from 'hooks/dataAccess/Workato/useRunCustomerRecipe';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import { BasePanel, RESTDataSource } from '../Dashboard/types';
import { useReactQuery } from '../Dashboard/useReactQuery';
import { ApiOptions } from './Field.type';

export type FormSelectHandlerProps = FormSelectProps & {
    operation?: ApiOptions['operation'];
};

const LoadingIndicator = () => (
    <CircularProgress size="16px" sx={{ marginRight: '8px' }} />
);

const asmActions: Record<string, RESTDataSource> = {
    getApplications: {
        method: 'GET',
        params: {
            applicationId: '*',
            includeApplication: true,
            includeClients: true,
            includeManagementStage: true,
            includeRemoved: true,
            includeUncataloged: true,
            includeUncategorizedWorkLogins: true,
            platformTenantId: '{{clientId}}',
            size: -1
        },
        requiredParams: ['platformTenantId'],
        source: 'rest',
        transformer: {
            expression:
                'data.map(x, {"label": x.name, "value": x.id, "image": x.application.favico})',
            type: 'CEL'
        },
        url: '/tenantApp'
    },
    getCategories: {
        method: 'GET',
        params: {
            category: '*',
            distinct: 'category',
            fields: '[category]',
            size: -1,
            sort: 'category'
        },
        requiredParams: ['category'],
        source: 'rest',
        transformer: {
            expression:
                'data.map(x, {"label": x.category, "value": x.category})',
            type: 'CEL'
        },
        url: '/category'
    },
    getUsers: {
        method: 'GET',
        params: {
            includeClients: true,
            platformTenantId: '{{clientId}}',
            size: -1,
            smartSort: true
        },
        requiredParams: ['platformTenantId'],
        source: 'rest',
        transformer: {
            expression: 'data.map(x, {"label": x.name, "value": x.email})',
            type: 'CEL'
        },
        url: '/user'
    }
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- fix type
const RenderAppIcon = (props: any) => (
    <ReactSelectComponents.Option {...props}>
        <Box {...props.innerProps}>
            {props.data.image && (
                <Image
                    imageId={props.data.image}
                    size="16"
                    style={{
                        borderRadius: '50%',
                        marginRight: 6
                    }}
                />
            )}
            {props.children}
        </Box>
    </ReactSelectComponents.Option>
);

const FormSelectAsmDataLoader = (props: FormSelectHandlerProps) => {
    const { clientId } = useFormFactoryContext();
    const { operation } = props;
    const { action } = operation!;

    const dataSource = {
        data: {
            ...asmActions[action!],
            ...(operation?.transform
                ? {
                      transformer: {
                          expression: operation.transform,
                          type: 'CEL'
                      }
                  }
                : {})
        }
    };

    const { data, isPending } = useReactQuery(
        dataSource as BasePanel,
        { variables: { clientId } },
        null
    );

    const options = useMemo(() => data, [data]);

    return (
        <IcebergFormSelect
            {...props}
            components={{
                LoadingIndicator,
                Option: RenderAppIcon
            }}
            loading={isPending}
            showLoadingState={isPending}
            options={options}
            creatable={props.creatable}
            isClearable={props.isClearable}
            isMulti={props.isMulti}
        />
    );
};

const FormSelectWorkatoDataLoader = (props: FormSelectHandlerProps) => {
    const [options, setOptions] = useState<SelectOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    const { operation } = props;
    const { action, valueAccessor, label } = operation!;
    const searchOperation = (operation?.search ||
        {}) as ApiOptions['operation']['search'];

    const isApiSearchEnabled = searchOperation?.enabled || false;
    const searchField = searchOperation?.searchField || '';

    // Create a debounced version of setSearch
    const debouncedSetSearch = useMemo(
        () =>
            _.debounce((value: string) => {
                setSearch(value);
            }, 300),
        []
    );

    // Cleanup the debounced function on unmount
    useEffect(
        () => () => {
            debouncedSetSearch.cancel();
        },
        [debouncedSetSearch]
    );

    const { applicationId, clientId } = useFormFactoryContext();

    if (!applicationId) {
        // TODO: Should we log an error here or throw an error?
        debug.error('Application ID is required');
    }

    const { runRecipes } = useRunCustomerRecipes();

    const runRecipeParams = useMemo(() => {
        const params = {
            action,
            applicationId,
            platformTenantId: clientId
        } as RunRecipeParams;

        if (isApiSearchEnabled && searchField) {
            params.recipeParams = {
                [searchField]: search
            };
        }

        return params;
    }, [
        action,
        clientId,
        applicationId,
        search,
        isApiSearchEnabled,
        searchField
    ]) as RunRecipeParams;

    useEffect(() => {
        const fetchOptions = async () => {
            setLoading(true);
            try {
                const results = await runRecipes(runRecipeParams);
                // @ts-expect-error fix type - I don't think we'll be able to fix this...
                const entities = results?.[0]?.data?.entities;

                if (entities?.length) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const selectOptions = entities.map((entity: any) => ({
                        label: entity[label || valueAccessor!],
                        value: entity[valueAccessor!]
                    })) as SelectOption[];

                    setOptions((prevOptions: SelectOption[]) =>
                        _.uniqBy([...prevOptions, ...selectOptions], 'value')
                    );
                }
            } catch (e) {
                // TODO: handle error
                debug.error(e);
            } finally {
                setLoading(false);
            }
        };

        fetchOptions();
    }, [label, valueAccessor, runRecipes, runRecipeParams]);

    return (
        <IcebergFormSelect
            {...props}
            onInputChange={(value) => {
                const trimmedValue = (value || '').trim();
                if (
                    isApiSearchEnabled &&
                    !!trimmedValue &&
                    trimmedValue !== search
                ) {
                    debouncedSetSearch(trimmedValue);
                }
            }}
            components={{
                LoadingIndicator
            }}
            loading={loading}
            showLoadingState={loading}
            options={options}
        />
    );
};

const FormSelectWithDataLoader = (props: FormSelectHandlerProps) => {
    const { operation } = props;
    const { serviceType } = operation!;

    if (serviceType === 'workato') {
        return <FormSelectWorkatoDataLoader {...props} />;
    }

    if (serviceType === 'asm') {
        return <FormSelectAsmDataLoader {...props} />;
    }

    return <IcebergFormSelect {...props} />;
};

export const FormSelectHandler = (props: FormSelectHandlerProps) => {
    // TODO: Should we check for more than just operation?
    if (props.operation) {
        return <FormSelectWithDataLoader {...props} />;
    }

    return <IcebergFormSelect {...props} />;
};
